<script lang="ts" module>
  declare let window: any
  declare var Stripe: any
</script>

<script lang="ts">
  import { onMount } from "svelte"
  import { Loader } from "lucide-svelte"
  import Layout from "$app/Layouts/Simple.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import {
    Medal,
    NotebookText,
    Users,
    FileText,
    Smartphone,
    Lock,
    GraduationCap
  } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import { formatter, xhr, toast } from "$app/utils"

  import hero_img from "@images/alignment_test/value-alignment.jpg"
  import hero_bg_img from "@images/alignment_test/rowing.jpg"

  import business_logo1 from "@images/companies/cba.png"
  import business_logo2 from "@images/companies/cfg-logo.png"
  import business_logo3 from "@images/companies/accutrans.png"
  import business_logo4 from "@images/companies/classic-plan.png"
  import business_logo5 from "@images/companies/horizon-hobby.png"
  import business_logo6 from "@images/companies/prevost.png"

  let props = $props()
  let stripe_publish_key: string = props.stripe_publish_key
  let recaptcha_site_key_v3: string = props.recaptcha_site_key_v3
  let stripe_product: any = props.stripe_product
  let errors: any = $state(props.errors || {})
  let params: any = props.params || {}

  let form: HTMLFormElement
  let stripe_token: string = $state("")
  let cardErrors: any
  let cardTokenInput: string
  let saving = $state(false)
  let mounted = false
  let auth_token: string = $state("")
  let recaptcha_token: string = $state("")

  let productPrice: number = $state(stripe_product.unit_amount / 100)
  let discountAmount: number = $state(0)
  let totalAmount: number = $derived(productPrice)

  let submitTimes = 0
  function handleSubmit(): void {
    window.grecaptcha
      .execute(recaptcha_site_key_v3, { action: "signup" })
      .then((token) => {
        // To test if the recaptcha submits again
        // appStore.form["g-recaptcha-response-data"] = token + 'FAIL'

        recaptcha_token = token
        submitTimes++
        submitForm()
      })
  }

  async function submitForm(): Promise<void> {
    saving = true

    // Attempt to tokenize the card number
    try {
      let result: any = { token: { id: "" } }
      result = await stripe.createToken(cardElement)

      if (result.error) {
        // On error, display the error message.
        cardErrors.classList.remove("hidden")
        cardErrors.textContent = result.error.message
        throw new Error("Card error")
      } else {
        // On success, add the token to the hidden field and submit to the server
        stripe_token = result.token.id

        setTimeout(() => {
          saving = true
          form.submit()
        }, 50)
      }
    } catch (e) {
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  let stripe: any
  let cardElement: any

  const clearCardError = () => {
    cardErrors.classList.add("hidden")
  }

  /**
   * Stripe
   */
  function loadStripeElements() {
    // Time for Stripe.js to do its magic.
    stripe = Stripe(stripe_publish_key)
    let elements = stripe.elements()

    let style = {
      base: {
        fontSize: "16px",
        color: "#32325d",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#a0aec0"
        }
      }
    }

    cardElement = elements.create("card", { style: style })
    cardElement.mount("#card-element")
    cardElement.on("change", clearCardError)
  }

  onMount(async () => {
    auth_token = (<HTMLMetaElement>(
      document.querySelector("meta[name=csrf-token]")
    )).content

    mounted = true

    if (stripe_coupon && !checked_coupon) {
      checkCoupon()
    }
  })

  let validate_btn
  let stripe_coupon: string = $state("")
  let validating = $state(false)
  let checked_coupon = $state(false)

  let coupon = $state({})

  /**
   * Review valid coupon
   */
  async function checkCoupon(): Promise<void> {
    stripe_coupon = stripe_coupon?.trim()

    if (stripe_coupon?.length > 0) {
      checked_coupon = true
      validating = true

      try {
        const resp = await xhr.get(
          `/stripe/promotion_code/${stripe_coupon.trim()}`
        )
        let promotion_code = resp.data

        if (promotion_code.valid) {
          coupon = promotion_code.coupon
          discountAmount = (productPrice * (coupon.percent_off || 0)) / 100
          totalAmount = productPrice - discountAmount
        } else {
          coupon.valid = false
        }

        console.log(promotion_code)
      } catch (e) {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
        toast(
          "There was an error validating the coupon code try again",
          "error"
        )
      } finally {
        validating = false
      }
    } else {
      coupon = {}
    }
  }

  function focusCoupon() {
    validate_btn.classList.remove("btn-secondary")
    validate_btn.classList.add("btn-primary")
  }

  function stripeLoaded(): void {
    // The external Stripe javascript is ready.
    // stripeReady = true
    const interval = setInterval(() => {
      if (mounted) {
        loadStripeElements()
        clearInterval(interval)
      }
    }, 200)
  }
</script>

<svelte:head>
  <script
    src="https://www.google.com/recaptcha/api.js?render={recaptcha_site_key_v3}"
    async
    defer
  ></script>
  <script src="https://js.stripe.com/v3/" onload={stripeLoaded}></script>
</svelte:head>

<Layout {...props}>
  <div class="relative bg-lwp-theme-blue">
    <div class="mx-auto max-w-7xl lg:grid lg:grid-cols-12 lg:gap-x-8 lg:px-8">
      <div
        class="px-6 pb-24 pt-10 sm:pb-32 lg:col-span-7 lg:px-0 lg:pb-56 lg:pt-32 xl:col-span-6"
      >
        <div class="mx-auto max-w-2xl lg:mx-0">
          <h1
            class="mt-24 text-4xl font-bold tracking-tight text-amber-300 sm:mt-10 sm:text-6xl uppercase"
          >
            Get your team on the same page.
          </h1>
          <p class="mt-12 text-2xl lg:text-4xl text-gray-200">
            The Lead With Purpose Alignment Blueprint will get your entire team
            aligned and rowing in the same direction.
          </p>
        </div>
      </div>
      <div
        class="relative lg:col-span-5 lg:-mr-8 xl:absolute xl:inset-0 xl:left-1/2 xl:mr-0"
      >
        <img
          class="aspect-[3/2] w-full bg-gray-50 object-cover lg:absolute lg:inset-0 lg:aspect-auto lg:h-full"
          src={hero_bg_img}
          alt=""
        />
      </div>
    </div>
  </div>

  <div
    class="mt-6 py-12 lg:py-16 mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-5xl"
  >
    <h1
      class="text-center text-black text-2xl font-bold sm:text-3xl md:text-4xl lg:text-5xl"
    >
      <div class="block mt-6">
        Begin the journey to get everyone engaged, empowered and encouraged
        through our simple 3-step process.
      </div>
    </h1>
  </div>
  <div class="bg-white mt-8">
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <div
        class="mx-auto grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3"
      >
        <article
          class="relative isolate flex flex-col justify-start overflow-hidden rounded-2xl bg-lwp-theme-blue px-8 pb-8 py-8"
        >
          <div
            class="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40"
          ></div>
          <div
            class="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10"
          ></div>

          <div
            class="flex flex-wrap items-center gap-y-1 overflow-hidden text-3xl text-gray-300"
          >
            Step 1: Determine Your Pre-Alignment Score
          </div>
          <h3 class="mt-3 text-lg font-semibold leading-6 text-white">
            <p>
              Discover the truth of your team's alignment with our simple and
              straight-forward alignment test. Invite an unlimited number of
              your leadership team to our automated test process which will
              record responses and calculate your team's alignment score.
            </p>
            <p class="mt-4">
              Sign up below for the one-time cost of $499 and invite your whole
              team to begin the process. Each team member will answer 6 critical
              questions which will help determine your team's alignment score.
            </p>
          </h3>
        </article>

        <article
          class="relative isolate flex flex-col justify-start overflow-hidden rounded-2xl bg-lwp-theme-blue px-8 pb-8 py-8"
        >
          <div
            class="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40"
          ></div>
          <div
            class="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10"
          ></div>

          <div
            class="flex flex-wrap items-center gap-y-1 overflow-hidden text-3xl text-gray-300"
          >
            Step 2: Craft Your One Page Alignment Plan
          </div>
          <h3 class="mt-3 text-lg font-semibold leading-6 text-white">
            <p>
              Once you have the clarity on the current state of alignment for
              your team, you can work on improving. Our alignment wizard will
              walk you through the process of crafting your One Page Alignment
              Plan.
            </p>
            <p class="mt-4">
              Distilled from Marc Koehler's experience as US Nuclear Submarine
              Officer, this proven plan will help to get your entire team
              aligned around a common mission. If you need extra help in this
              part of the process we have courses which will give you detailed
              training and you even hire Marc for 1-on-1 consulting.
            </p>
          </h3>
        </article>

        <article
          class="relative isolate flex flex-col justify-start overflow-hidden rounded-2xl bg-lwp-theme-blue px-8 pb-8 py-8"
        >
          <div
            class="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40"
          ></div>
          <div
            class="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10"
          ></div>

          <div
            class="flex flex-wrap items-center gap-y-1 overflow-hidden text-3xl text-gray-300"
          >
            Step 3: Calculate Your Post-Alignment Score
          </div>
          <h3 class="mt-3 text-lg font-semibold leading-6 text-white">
            <p>
              Once you have crafted your One Page Plan and gotten your team
              connected and aligned to the mission, it is time to discover how
              your team's alignment has improved through the Lead With Purpose
              process.
            </p>
            <p class="mt-4">
              Your team will be asked to take the Post-Alignment Test which will
              reveal a new alignment score for your team.
            </p>
          </h3>
        </article>
      </div>
    </div>
  </div>

  <div class="relative mt-12">
    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex"
      >
        <div class="bg-lwp-theme-blue px-6 py-8 lg:w-7/12 lg:p-12">
          <h3 class="text-2xl leading-8 text-white sm:text-3xl sm:leading-9">
            The Alignment Blueprint Program
          </h3>
          <p class="mt-6 text-base leading-6 text-gray-100">
            With your single, one-time purchase you will be able to invite your
            entire team to both the Pre and Post Alignment Tests. Additionally
            you will be able to create a Free 3-Member Lead With Purpose Plan to
            craft your One Page Alignment Plan.
          </p>
          <div class="mt-8">
            <div class="flex items-center">
              <h4
                class="flex-shrink-0 pr-4 text-sm leading-5 tracking-wider font-semibold uppercase text-blue-100"
              >
                What's included
              </h4>
              <div class="flex-1 border-t-2 border-blue-200"></div>
            </div>
            <ul class="mt-10">
              <li>
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div
                      class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                    >
                      <NotebookText class="text-amber-800 w-8 h-8" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <h5 class="text-xl leading-6 font-medium text-gray-100">
                      Pre-Alignment and Post-Alignment Tests for Entire Team
                    </h5>
                  </div>
                </div>
              </li>
              <li class="mt-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div
                      class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                    >
                      <Users class="text-amber-800 w-8 h-8" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <h5 class="text-xl leading-6 font-medium text-gray-100">
                      Free 3-Member Lead With Purpose Plan
                    </h5>
                  </div>
                </div>
              </li>
              <li class="mt-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div
                      class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                    >
                      <Medal class="text-amber-800 w-8 h-8" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <h5 class="text-xl leading-6 font-medium text-gray-100">
                      Pre-Alignment and Post-Alignment Scoring and Result Report
                    </h5>
                  </div>
                </div>
              </li>
              <li class="mt-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div
                      class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                    >
                      <FileText class="text-amber-800 w-8 h-8" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <h5 class="text-xl leading-6 font-medium text-gray-100">
                      Printable One Page Alignment Plan
                    </h5>
                  </div>
                </div>
              </li>
              <li class="mt-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div
                      class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                    >
                      <Smartphone class="text-amber-800 w-8 h-8" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <h5 class="text-xl leading-6 font-medium text-gray-100">
                      Lead With Purpose Mobile App Access
                    </h5>
                  </div>
                </div>
              </li>
              <li class="mt-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div
                      class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                    >
                      <GraduationCap class="text-amber-800 w-8 h-8" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <h5 class="text-xl leading-6 font-medium text-gray-100">
                      Access to the Lead With Purpose University
                    </h5>
                  </div>
                </div>
              </li>

              <li class="mt-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div
                      class="flex items-center justify-center h-12 w-12 rounded-md bg-amber-500 text-white"
                    >
                      <Lock class="text-amber-800 w-8 h-8" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <h5 class="text-xl leading-6 font-medium text-gray-100">
                      Ability to upgrade to Paid Lead With Purpose Plan
                    </h5>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div class="flex items-center mt-12">
            <h4
              class="flex-shrink-0 pr-4 text-sm leading-5 tracking-wider font-semibold uppercase text-blue-100"
            >
              Satisfaction Guaranteed
            </h4>
            <div class="flex-1 border-t-2 border-blue-200"></div>
          </div>
          <div class="flex mt-6">
            <div class="flex-grow">
              <p class="text-lg leading-6 text-blue-100">
                If you're not satisfied, then we're not satisifed. We offer a
                14-day 100% no questions asked money back guarantee.
              </p>
            </div>
          </div>
        </div>
        <div
          class="py-8 px-6 text-center bg-gray-100 lg:w-5/12 lg:flex lg:flex-col lg:justify-center lg:p-12"
        >
          <p class="text-lg leading-6 font-medium text-gray-800">
            Single flat fee for any team size
          </p>
          <div
            class="mt-4 flex items-center justify-center text-5xl leading-none font-extrabold text-gray-900"
          >
            <span class="ml-2"> $499 </span>
            <span class="ml-3 text-xl leading-7 font-medium text-gray-500">
              USD
            </span>
          </div>
          <div class="mt-6">
            <div class="rounded-md text-left">
              <h2
                class="text-3xl leading-9 font-extrabold text-gray-800 text-center"
              >
                Sign up today!
              </h2>

              <form
                onsubmit={(e) => {
                  e.preventDefault()
                  handleSubmit()
                }}
                method="post"
                action="/alignment_signup"
                bind:this={form}
                id="payment-form"
              >
                {#if Object.keys(errors).length > 0}
                  <div class="bg-red-600 text-white text-sm p-3 mb-2">
                    There are some errors in the form that prohibited to create
                    the account, please correct the errors shown below:
                  </div>
                {/if}

                {#if errors.recaptcha}
                  <div class="text-red-600 text-center font-semibold my-4">
                    {errors.recaptcha}
                  </div>
                {/if}

                <input
                  type="hidden"
                  name="purchase[stripe_product_id]"
                  value={stripe_product.stripe_id}
                />
                {#if coupon.id}
                  <input
                    type="hidden"
                    name="purchase[stripe_coupon]"
                    value={coupon.id}
                  />
                {/if}
                <input
                  type="hidden"
                  name="purchase[stripe_price_id]"
                  value={stripe_product.price_id}
                />

                <input
                  type="hidden"
                  name="purchase[stripe_token]"
                  value={stripe_token}
                />

                <input
                  name="authenticity_token"
                  type="hidden"
                  bind:value={auth_token}
                />

                <input
                  type="hidden"
                  name="g-recaptcha-response-data"
                  bind:value={recaptcha_token}
                />

                <div class="input-cont" class:error={errors.email}>
                  <label
                    for="user[email]"
                    class="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <div class="mt-1">
                    <input
                      id="email"
                      name="user[email]"
                      type="email"
                      required
                      class="input rounded-md"
                      value={params?.user?.email ? params.user.email : ""}
                    />
                  </div>
                  <div class="msg">{errors.email}</div>
                </div>

                <div class="input-cont mt-4" class:error={errors.name}>
                  <label
                    for="user[name]"
                    class="block text-sm font-medium text-gray-700"
                  >
                    Your full name
                  </label>
                  <div class="mt-1">
                    <input
                      id="full_name"
                      name="user[name]"
                      type="text"
                      required
                      class="input rounded-md"
                      value={params?.user?.name ? params.user.name : ""}
                    />
                  </div>
                  <div class="msg">{errors.name}</div>
                </div>

                <div class="input-cont mt-4" class:error={errors.password}>
                  <label
                    for="user[password]"
                    class="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <div class="mt-1">
                    <input
                      id="password"
                      name="user[password]"
                      type="password"
                      autocomplete="current-password"
                      required
                      class="input rounded-md"
                    />
                  </div>
                  <div class="msg">{errors.password}</div>
                </div>

                <div class="space-y-2 mt-6">
                  <h3
                    class="text-2xl font-extrabold tracking-tight mt-2 text-gray-600"
                  >
                    Payment Details
                  </h3>

                  <div class="mt-4 flex justify-between py-2">
                    <div class="text-left text-lg">
                      <p>Alignment Test</p>
                    </div>
                    <div class="text-right text-lg font-bold">
                      <p>{formatter.format(productPrice)} USD</p>
                    </div>
                  </div>

                  {#if discountAmount}
                    <div class="mt-4 flex justify-between py-2">
                      <div class="text-left text-base italic text-grey-700">
                        <p>Discount</p>
                      </div>
                      <div
                        class="text-right text-base italic text-gray-700 font-bold"
                      >
                        <p>-{formatter.format(discountAmount)}</p>
                      </div>
                    </div>
                  {/if}
                  <div
                    class="mt-6 mb-4 py-2 flex justify-between border-t border-dotted border-gray-400"
                  >
                    <div class="text-left text-xl">
                      <p>Today's Total</p>
                    </div>
                    <div class="text-right text-xl font-bold">
                      <p>{formatter.format(totalAmount)} USD</p>
                    </div>
                  </div>

                  <div class="mt-4 grid grid-cols-1 gap-y-4 gap-x-4">
                    <div class="border-t pt-2">
                      <div class="flex coupon-label">
                        <label for="subscription[stripe_coupon]">Coupon </label>
                        {#if coupon.valid === true}
                          <div class="text-green-600 text-right w-full">
                            Valid Coupon
                          </div>
                        {:else if coupon.valid === false}
                          <div class="text-red-600 text-right w-full">
                            Invalid Coupon
                          </div>
                        {/if}
                      </div>
                      <div class="flex mt-1">
                        <input
                          type="text"
                          name="subscription[stripe_coupon]"
                          bind:value={stripe_coupon}
                          class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md rounded-r-none px-2 py-1 bg-white"
                          class:border-red-600={coupon.valid === false}
                          class:border-green-600={coupon.valid === true}
                          placeholder="Coupon code"
                          onblur={checkCoupon}
                          onfocus={focusCoupon}
                        />
                        <button
                          onclick={(e) => {
                            e.preventDefault()
                            checkCoupon()
                          }}
                          bind:this={validate_btn}
                          class="btn btn-secondary whitespace-nowrap !rounded-l-none"
                          disabled={validating}
                        >
                          Validate Coupon
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class:hidden={totalAmount == 0}>
                    <!-- We'll put the error messages in this element -->
                    <div bind:this={cardErrors} role="alert"></div>
                    <div class="mt-4 grid grid-cols-1 gap-y-4 gap-x-4">
                      <div class="pb-2">
                        <label
                          for="first_name"
                          class="block text-sm font-medium text-gray-700"
                        >
                          Credit Card
                        </label>
                        <div
                          class="mt-1 p-2 border shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white"
                        >
                          <div id="card-element" class=""></div>
                          <input type="hidden" bind:this={cardTokenInput} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-6">
                  <div class="flex items-start">
                    <div class="h-5 flex items-center">
                      <input
                        id="email_optin"
                        name="email_optin"
                        type="checkbox"
                        class="mt-1 focus:ring-blue-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                    </div>
                    <div class="ml-4 text-sm">
                      <label
                        for="email_optin"
                        class="font-medium text-gray-700 pt-0"
                        >Yes, please sign me up for the mailing list to receive
                        the latest news from Lead with Purpose</label
                      >
                    </div>
                  </div>
                </div>

                <div class="mt-6">
                  <button
                    type="submit"
                    disabled={saving || coupon.valid === false}
                    class="btn big bg-lwp-blue hover:bg-blue-700 text-white inline font-bold text-center w-full justify-center"
                  >
                    {#if saving}
                      <Loader class="animate-spin" />
                      Please wait...
                    {:else}
                      Sign Up
                    {/if}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="bg-white">
    <div class="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
      <h2 class="text-3xl font-extrabold text-black text-center">
        Trusted by Thousands of Businesses
      </h2>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
        <div class="flex justify-center items-center">
          <img src={business_logo1} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo2} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo3} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo4} class="mx-auto max-h-12 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo5} class="mx-auto max-h-10 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo6} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
      </div>
    </div>
  </div>

  <div class="py-12 px-24 max-w-6xl mx-auto">
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div>
        <div
          class="mx-auto text-black text-5xl font-bold flex items-center justify-center"
        >
          1000s
        </div>
        <div class="text-gray-900 text-center mt-3">
          Big and Small Companies Helped
        </div>
        <div class="flex justify-center items-center mt-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-10 h-10"
            aria-hidden="true"
            role="img"
            preserveAspectRatio="xMidYMid meet"
            viewBox="0 0 24 24"
            ><path
              d="M15 3a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-2v2h4a1 1 0 0 1 1 1v3h2a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2v-2H8v2h2a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2v-3a1 1 0 0 1 1-1h4V9H9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6zM9 17H5v2h4v-2zm10 0h-4v2h4v-2zM14 5h-4v2h4V5z"
              fill="currentColor"
            /></svg
          >
        </div>
      </div>
      <div>
        <div
          class="mx-auto text-black text-5xl font-bold flex items-center justify-center"
        >
          100s
        </div>
        <div class="text-gray-900 text-center mt-3">
          Instructional Videos Created
        </div>
        <div class="flex justify-center items-center mt-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-10 h-10"
            aria-hidden="true"
            role="img"
            preserveAspectRatio="xMidYMid meet"
            viewBox="0 0 1024 1024"
            ><path
              d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448s448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372s372 166.6 372 372s-166.6 372-372 372z"
              fill="currentColor"
            /><path
              d="M719.4 499.1l-296.1-215A15.9 15.9 0 0 0 398 297v430c0 13.1 14.8 20.5 25.3 12.9l296.1-215a15.9 15.9 0 0 0 0-25.8zm-257.6 134V390.9L628.5 512L461.8 633.1z"
              fill="currentColor"
            /></svg
          >
        </div>
      </div>
      <div>
        <div
          class="mx-auto text-black text-5xl font-bold flex items-center justify-center"
        >
          40,000+
        </div>
        <div class="text-gray-900 text-center mt-3">Employees Trained</div>
        <div class="flex justify-center items-center mt-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-10 h-10"
            aria-hidden="true"
            role="img"
            preserveAspectRatio="xMidYMid meet"
            viewBox="0 0 48 48"
            ><g fill="none"
              ><path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6 6h31v5h-2V8H8v23h21.387v2H6V6zm30 13a3 3 0 1 0 0-6a3 3 0 0 0 0 6zm2.031 2.01c1.299 0 2.327.584 3 1.486c.629.845.895 1.89.955 2.855c.061.979-.08 2.005-.397 2.92c-.3.87-.807 1.77-1.589 2.387V40.5a1.5 1.5 0 0 1-2.98.247L35.73 33h-.298l-1.458 7.776A1.5 1.5 0 0 1 31 40.5V26.233a63.223 63.223 0 0 0-.592.919l-.078.123l-.02.032l-.005.009a1.5 1.5 0 0 1-1.274.707h-5a1.5 1.5 0 1 1 0-3h4.177c.243-.376.563-.864.899-1.354c.35-.511.736-1.052 1.08-1.476c.167-.207.354-.423.542-.6c.092-.087.22-.2.376-.3a1.72 1.72 0 0 1 .926-.282h6z"
                fill="currentColor"
              /></g
            ></svg
          >
        </div>
      </div>
    </div>
  </div>

  <div class="relative mt-12">
    <div class="max-h-96 overflow-hidden">
      <img
        src={hero_img}
        class="w-full h-full object-center"
        alt="Alignment Test"
      />
    </div>
  </div>
</Layout>
